import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Mail, Phone, Mic, BookOpen, Brain, Users } from 'lucide-react';
import { motion } from 'framer-motion';

const AboutPage = () => {
  return (
    <div className="f500-page min-h-screen bg-[var(--isp-page)]" data-testid="about-page">
      {/* Top bar */}
      <header className="border-b border-[var(--f500-line)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[var(--f500-ink-2)] hover:text-[var(--f500-ink)] transition-colors text-sm"
            style={{ fontFamily: 'var(--sf-text)' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero — Swiss editorial */}
      <section className="border-b border-[var(--f500-line)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <p className="f500-eyebrow mb-6" data-testid="about-eyebrow">— About / iStudyPro</p>
              <motion.h1
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="f500-display text-5xl sm:text-6xl lg:text-7xl"
              >
                Built so students<br />never study alone.
              </motion.h1>
            </div>
            <div className="lg:col-span-4 lg:pl-8 lg:border-l lg:border-[var(--f500-line)]">
              <p className="f500-body text-base">
                We make AI-powered study tools that help university students transcribe lectures, generate flashcards, and rehearse for exams — without paywalls between them and their grades.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission columns */}
      <section className="border-b border-[var(--f500-line)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[var(--f500-line)]">
            {[
              { eyebrow: '01 / Mission', title: 'Empower the underdog.', body: 'To put cutting-edge AI study tools in the hands of every student — not just those at top-ranked schools or with deep pockets.' },
              { eyebrow: '02 / Values', title: 'Privacy by design.', body: 'Your lecture audio, notes, and chat are encrypted at rest. We never train foundation models on your data. Period.' },
              { eyebrow: '03 / Community', title: 'Built with students.', body: 'Every feature is shaped by real student feedback — from study rooms to spaced repetition. We ship what works.' },
            ].map((col, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`p-8 lg:p-12 bg-[var(--isp-page)] ${i < 2 ? 'md:border-r border-[var(--f500-line)]' : ''} ${i < 2 ? 'border-b md:border-b-0 border-[var(--f500-line)]' : ''}`}
              >
                <p className="f500-eyebrow mb-4">{col.eyebrow}</p>
                <h3 className="f500-h2 text-2xl mb-3">{col.title}</h3>
                <p className="f500-body text-sm">{col.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-[var(--f500-line)] bg-[var(--f500-paper-2)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
            <div className="lg:col-span-4">
              <p className="f500-eyebrow mb-3">— What we ship</p>
              <h2 className="f500-h1 text-4xl">Tools you'll actually use.</h2>
            </div>
            <div className="lg:col-span-8 lg:pt-4">
              <p className="f500-body text-base">
                Four pillars, one workflow. Record your lecture, let AI structure it, drill flashcards, then jump into a study room when you need accountability.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-[var(--f500-line)] bg-[var(--isp-page)]">
            {[
              { icon: Mic, num: '01', title: 'Lecture transcription', body: 'Whisper-grade accuracy. Local + cloud modes.' },
              { icon: BookOpen, num: '02', title: 'Smart flashcards', body: 'SM-2 spaced repetition baked in.' },
              { icon: Brain, num: '03', title: 'AI study buddy', body: '24/7 tutor grounded in your notes.' },
              { icon: Users, num: '04', title: 'Study rooms', body: 'Pomodoro with friends. Real accountability.' },
            ].map((f, i) => (
              <div key={i} className={`p-6 lg:p-8 ${i < 3 ? 'lg:border-r' : ''} ${i < 2 ? 'md:border-r' : ''} ${i < 2 ? 'md:border-b lg:border-b-0' : ''} border-[var(--f500-line)]`}>
                <p className="f500-mono text-xs text-[var(--f500-ink-3)] mb-4">{f.num}</p>
                <f.icon className="w-5 h-5 text-[var(--f500-ink)] mb-4" strokeWidth={1.75} />
                <h3 className="f500-h3 text-base mb-2">{f.title}</h3>
                <p className="f500-body text-sm">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="border-b border-[var(--f500-line)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <p className="f500-eyebrow mb-3">— Contact</p>
              <h2 className="f500-h1 text-4xl">Tell us what's broken.</h2>
              <p className="f500-body text-base mt-3 max-w-xl">
                Bug? Feature request? Partnership? We read every email — and our average reply time is under 24 hours.
              </p>
            </div>
            <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-0 border border-[var(--f500-line)]">
              <a
                href="mailto:info@istudypro.net"
                className="flex flex-col gap-2 p-6 hover:bg-[var(--f500-paper-2)] transition-colors sm:border-r border-[var(--f500-line)]"
                data-testid="about-contact-email"
              >
                <Mail className="w-4 h-4 text-[var(--f500-ink-3)]" strokeWidth={1.75} />
                <span className="f500-eyebrow">Email</span>
                <span className="text-sm text-[var(--f500-ink)] font-semibold" style={{ fontFamily: 'var(--sf-text)' }}>info@istudypro.net</span>
              </a>
              <a
                href="tel:+233246257382"
                className="flex flex-col gap-2 p-6 hover:bg-[var(--f500-paper-2)] transition-colors border-t sm:border-t-0 border-[var(--f500-line)]"
                data-testid="about-contact-phone"
              >
                <Phone className="w-4 h-4 text-[var(--f500-ink-3)]" strokeWidth={1.75} />
                <span className="f500-eyebrow">Phone</span>
                <span className="text-sm text-[var(--f500-ink)] font-semibold" style={{ fontFamily: 'var(--sf-text)' }}>+233 (024) 625-7382</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA strip — inverse */}
      <section className="bg-[var(--f500-ink)] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-8">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60 mb-4">— Ready when you are</p>
              <h2 className="f500-h1 text-white text-4xl sm:text-5xl">
                Stop reading lecture notes.<br />Start mastering them.
              </h2>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/login" className="f500-btn f500-btn-inverse" data-testid="about-cta">
                Get Started <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[var(--isp-page)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 text-center">
          <p className="text-xs text-[var(--f500-ink-3)]" style={{ fontFamily: 'var(--sf-text)' }}>
            © 2026 iStudyPro. All rights reserved. <span className="mx-2">·</span>
            <a href="https://www.istudypro.net" className="hover:text-[var(--f500-ink)] transition-colors">www.istudypro.net</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
